import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, GAPS_LIST, GRID_RADIUS, INK, NEUTRAL, RADIUS } from "../../../../theme/tokens";
import { SheetActionRow } from "../../../UIToolkit/Clickable/SheetActionRow";
import { SheetStep } from "../../../UIToolkit/Popups/SheetStep";
import { SheetStepButton } from "../../../UIToolkit/Popups/SheetStepButton";
import { GapsRoleBadge } from "../GapsRoleBadge";
import type { GapsStaffEntry } from "../gapsDayModel";
import { UNASSIGNED_LOCATION_NAME } from "../gapsDayModel";
import { GapsStaffStep } from "./gapsSheetTypes";
import { staffSheetSubtitle } from "./staffSheetCopy";

type Props = {
	entry: GapsStaffEntry;
	name: string;
	jobLabel: string;
	timeLabel: string;
	hoursLabel: string;
	onStep: (step: GapsStaffStep) => void;
	onClose: () => void;
};

const SHEET = "gaps-staff-actions";

/** The quoted note's trailing corners (px): a metadata chip's radius, so the quote reads as a card edge, not a pill. */
const NOTE_CORNER_PX = GRID_RADIUS.CHIP * RADIUS.CARD;

/**
 * What you can do about one person on a shift (P3-5): Remove · Change location · Change hours · More, in the
 * artifact's order, the destructive one first because it is why a head nurse opens this sheet most often (a call-in)
 * — set apart by its tone rather than buried at the bottom.
 *
 * The heading is the person: their role badge, their name, and the shift they are on. A note, when there is one, is
 * shown before the actions, because it is the kind of thing that changes which action you take.
 */
export const GapsStaffActionsStep: FC<Props> = ({ entry, name, jobLabel, timeLabel, hoursLabel, onStep, onClose }) => {
	const { t } = useTranslation();
	const shift = entry.slot.nurseInShift;
	const isUnassigned = entry.locationName === UNASSIGNED_LOCATION_NAME;

	return (
		<SheetStep
			stepKey={GapsStaffStep.Actions}
			dataTestId={SHEET}
			leading={<GapsRoleBadge role={shift.role} size="heading" />}
			title={name}
			subtitle={staffSheetSubtitle(t, { entry, jobLabel, timeLabel, hoursLabel })}
			footer={
				<SheetStepButton tone="dismiss" onClick={onClose} dataTestId={`${SHEET}-cancel`}>
					{t("close")}
				</SheetStepButton>
			}
		>
			{shift.notes && (
				<Box
					data-testid={`${SHEET}-note`}
					sx={{
						mx: 2,
						mt: 1.5,
						mb: 0.5,
						px: 1.25,
						py: 0.875,
						borderLeft: `${GAPS_LIST.EDGE_PX}px solid`,
						borderColor: "primary.light",
						bgcolor: NEUTRAL[50],
						borderRadius: `0 ${NOTE_CORNER_PX}px ${NOTE_CORNER_PX}px 0`,
					}}
				>
					<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.SECONDARY, lineHeight: 1.45, overflowWrap: "anywhere" }}>{shift.notes}</Typography>
				</Box>
			)}
			<SheetActionRow
				tone="danger"
				icon={<RemoveCircleOutlineOutlinedIcon />}
				label={t("gaps.staffSheet.remove")}
				onClick={() => onStep(GapsStaffStep.Remove)}
				dataTestId={`${SHEET}-remove`}
			/>
			<SheetActionRow
				icon={<PlaceOutlinedIcon />}
				label={t("gaps.staffSheet.changeLocation")}
				trailing={isUnassigned ? t("gaps.staffSheet.unassigned") : entry.locationName}
				showChevron
				onClick={() => onStep(GapsStaffStep.Location)}
				dataTestId={`${SHEET}-location`}
			/>
			<SheetActionRow
				icon={<ScheduleOutlinedIcon />}
				label={t("gaps.staffSheet.changeHours")}
				trailing={hoursLabel}
				showChevron
				onClick={() => onStep(GapsStaffStep.Hours)}
				dataTestId={`${SHEET}-hours`}
			/>
			<SheetActionRow
				icon={<MoreHorizIcon />}
				label={t("gaps.staffSheet.more")}
				description={t("gaps.staffSheet.moreHint")}
				showChevron
				onClick={() => onStep(GapsStaffStep.More)}
				dataTestId={`${SHEET}-more`}
			/>
		</SheetStep>
	);
};
